import type {Theme} from '@emotion/react';

interface VariablesProps {
    theme: Theme;
}

/**
 * Gets the base CSS variables for checkbox styling.
 *
 * @param props       The props object containing the theme.
 * @param props.theme The theme object containing color definitions.
 * @returns CSS string with base variables for checkbox styling.
 */
export const getBaseVariables = ({theme}: VariablesProps) => `
    --cursor: pointer;
    --input-background-color: ${theme.colors.neutral0};
    --input-border: 1px solid ${theme.colors.neutral60};
    --input-outline: none;
    --icon-display: none;
    --label-font-color: ${theme.colors.textPrimary};

    &[data-has-error=true] {
        --input-border: 1px solid ${theme.colors.error};
        --label-font-color: ${theme.colors.error};
    }
`;

/**
 * Gets the CSS variables for disabled checkbox state.
 *
 * @param props       The props object containing the theme.
 * @param props.theme The theme object containing color definitions.
 * @returns CSS string with disabled state variables for checkbox styling.
 */
export const getDisabledVariables = ({theme}: VariablesProps) => `
    --cursor: not-allowed;
    --input-background-color: ${theme.colors.neutral40};
    --input-border: 1px solid ${theme.colors.neutral40};
    --input-outline: none;
`;

/**
 * Gets the CSS variables for checkbox hover state.
 *
 * @param props       The props object containing the theme.
 * @param props.theme The theme object containing color definitions.
 * @returns CSS string with hover state variables for checkbox styling.
 */
export const getHoverVariables = ({theme}: VariablesProps) => `
    --input-border: 1px solid ${theme.colors.primary};
    --input-outline: 4px solid ${theme.colors.neutral20};

    &[data-has-error=true] {
        --input-border: 1px solid ${theme.colors.error};
    }
`;

/**
 * Gets the CSS variables for checkbox focus state.
 *
 * @param props       The props object containing the theme.
 * @param props.theme The theme object containing color definitions.
 * @returns CSS string with focus state variables for checkbox styling.
 */
export const getFocusVariables = ({theme}: VariablesProps) => `
    --input-border: 1px solid ${theme.colors.primary};
    --input-outline: 4px solid ${theme.colors.primary};
`;

/**
 * Gets the CSS variables for active/checked checkbox state.
 *
 * @param props       The props object containing the theme.
 * @param props.theme The theme object containing color definitions.
 * @returns CSS string with active state variables for checkbox styling.
 */
export const getActiveVariables = ({theme}: VariablesProps) => `
    --input-background-color: ${theme.colors.primary};
    --input-border: 1px solid ${theme.colors.primary};
    --icon-display: block;

    &[data-has-error=true] {
        --input-background-color: ${theme.colors.error};
        --input-border: 1px solid ${theme.colors.error};
        --label-font-color: ${theme.colors.error};
    }
`;